import type {Metadata} from "@khanacademy/wonder-stuff-core";

import {KAError} from "./ka-error.ts";
import {toKAError} from "./to-ka-error.ts";
import {
    Errors,
    type AmbiguousError,
    type ErrorKind,
    type IAmbiguousErrorLog,
    type IKAErrorLog,
    type LogErrorOptions,
} from "./types.ts";

/**
 * Log that supports ambiguous errors, converting them to simplified errors
 * for downstream logger use.
 */
export class AmbiguousErrorLog implements IAmbiguousErrorLog {
    _kaErrorLog: IKAErrorLog;

    constructor(kaErrorLog: IKAErrorLog) {
        this._kaErrorLog = kaErrorLog;
    }

    log: (
        message: string,
        metadata?: Metadata | null | undefined,
        ...args: Array<unknown>
    ) => void = (message, metadata, ...args): void =>
        this._kaErrorLog.log(message, metadata, ...args);

    error: (
        error: AmbiguousError,
        maybeKind?: ErrorKind | null | undefined,
        maybeOptions?: LogErrorOptions | null | undefined,
    ) => void = (error, maybeKind, maybeOptions): void => {
        /**
         * We have two possible IAmbiguousErrorLog signatures to implement here:
         *   1. KAError
         *   2. string | Error, Kind, [options]
         */

        // NOTE: if the error is an actual error, the `cause` option is going
        // to get ignored.
        const kaError =
            error instanceof KAError &&
            maybeOptions == null &&
            maybeKind == null
                ? error
                : toKAError(
                      error,
                      // Should always be a kind if we don't have a sole
                      // KAError. Let's set a default for cases where that is
                      // strangely not the case.
                      maybeKind ?? Errors.InvalidInput,
                      maybeOptions,
                  );

        // TODO(somewhatabstract, FEI-3999): Handling of ambiguous errors will
        // be provided by a builder eventually. At this point, we really
        // should have a KAError, otherwise something went horribly wrong.
        if (!(kaError instanceof KAError)) {
            throw new KAError(
                "Logged item is not a valid error",
                Errors.InvalidInput,
            );
        }

        this._kaErrorLog.error(kaError);
    };
}
